import { useEffect, useState } from 'react';
import {
  ThemeProvider,
  Typography,
  Button,
  AppHeaderBackButton,
  Link,
  Icon,
  Alert
} from '@ace-clublabs/shared-component-library';
import Header from '../components/Header';
import { IPaymentDetails } from '../shared/types';
import { PaymentMethodsAuthorizationModal } from '../components/PaymentMethodsAuthorizationModal';
import { DateHelper } from '@lib/shared';
import { useGlobalState } from '../shared/state';
import { UtagHelper } from '../utils/utagHelper';
import { useNavigate } from 'react-router-dom';

export interface PaymentReviewProps {
  amount: number;
  paymentDetails: IPaymentDetails;
  scheduledDate?: string | undefined;
  pit?: {
    amount: number | undefined;
    date: string | undefined;
    scheduledPaymentInfo?: { hasScheduledPayment?: boolean; scheduledDate?: string };
  };
  tokenProps?: { manifestId: string; returnUrl: string };
  adyenSessionData?: {
    sessionId: string;
    encryptedCardData?: any;
    encryptedAchData?: any;
  };
  onSubmit: (e: any) => void;
  onBack: (e: any) => void;
}

// to do: create story for soft errors
export const PaymentReview = ({
  amount,
  paymentDetails,
  scheduledDate,
  pit,
  tokenProps,
  adyenSessionData,
  onSubmit,
  onBack,
}: PaymentReviewProps) => {
  const [showPaymentAuthorization, setShowPaymentAuthorization] = useState(false);
  const [manifest] = useGlobalState('manifest');
  const [sessionId] = useGlobalState('sessionId');
  const [customerId] = useGlobalState('customerId');
  const [redirectMap, setRedirected] = useGlobalState('redirectMap');
  const schedule: boolean = scheduledDate ? isFutureDate(scheduledDate) : false;

  const navigate = useNavigate();

  const buttonText = schedule ? 'Schedule payment' : 'Pay now';
  let paymentType = ''; // to do credit/debit/prepaid as card info?
  switch (paymentDetails.paymentType) {
    case 'credit':
      paymentType = 'Credit card';
      break;
    case 'debit':
      paymentType = 'Debit card';
      break;
    case 'echeck':
      paymentType = 'E-check';
      break;
    case 'prepaid':
      paymentType = 'Prepaid card';
      break;
    case 'undefined':
      paymentType = 'Payment method';
      break;
    default:
      paymentType = 'Payment method';
      break;
  }

  // For non-Adyen flows, get card details from paymentDetails
  // For Adyen, card details are not available until backend processes payment
  const eCheckRoutingNumber = paymentDetails.echeck?.routingNumber || '';

  const tag = () => {
    if (customerId && sessionId) {
      const smsPaymentReviewTags = {
        channel: 'my account',
        custID: customerId,
        ...(manifest?.tags?.flow && { flow: manifest.tags.flow }),
        ...(manifest?.tags?.ins_policy && { ins_policy: manifest.tags.ins_policy }),
        page: 'my account:sms:payment review',
        sessionid: sessionId,
        subchannel1: 'sms',
      };

      UtagHelper.tag(smsPaymentReviewTags);
      return;
    }
    if (manifest?.tags) {
      const { subchannel1, subchannel2, channel } = manifest.tags || {};
      let page = subchannel2
        ? `${channel}:${subchannel1}:${subchannel2}:payment review`
        : `${channel}:${subchannel1}:payment review`;

      UtagHelper.tag({ ...manifest.tags, page, sessionid: sessionId });
    }
  };

  const getIconForCard = (cardBrand: string) => {
    if (paymentDetails.card?.brandImageUrl) {
      return <img src={paymentDetails.card.brandImageUrl} alt={cardBrand} className="cardBrand" />;
    } else {
      switch (cardBrand?.toLowerCase()) {
        case 'visa':
          return <Icon iconName="visa-logo" className="cardBrand" />;
        case 'american-express':
        case 'americanexpress':
        case 'amex':
          return <Icon iconName="amex-logo" className="cardBrand" />;
        case 'dinersclub':
        case 'discover':
          return <Icon iconName="discover-logo" className="cardBrand" />;
        case 'jcb':
          return <Icon iconName="jcb-logo" className="cardBrand" />;
        case 'chinaunionpay':
        case 'unionpay':
          return <Icon iconName="union-pay-logo" className="cardBrand" />;
        case 'maestro':
        case 'mastercard':
          return <Icon iconName="mastercard-logo" className="cardBrand" />;
        default:
          return null;
      }
    }
  };

  const last4 =
    paymentDetails.card && paymentDetails.card.last4
      ? paymentDetails.card.last4
      : paymentDetails.echeck && paymentDetails.echeck.last4
        ? paymentDetails.echeck.last4
        : '';

  useEffect(() => {
    tag();
  }, [customerId, sessionId]);

  const handleSubmit = (e: any) => {
    if (tokenProps) {
      const { manifestId, returnUrl } = tokenProps;
      setRedirected({ ...redirectMap, [manifestId]: returnUrl });
      navigate(`/redirect/${manifestId}`);
    } else {
      // Pass state object with payment details to onSubmit handler
      onSubmit({
        paymentDetails,
        adyenSessionData,
      });
    }
  };
  const pitText = (pit?.scheduledPaymentInfo?.scheduledDate && pit?.scheduledPaymentInfo?.hasScheduledPayment
    ? <span>Your payment of <strong>${pit?.amount?.toFixed(2)} is scheduled on {pit.scheduledPaymentInfo.scheduledDate}</strong>. If you continue, you'll be submitting an additional payment.</span>
    : <span>Your payment of <strong>${pit?.amount?.toFixed(2)} on {pit?.date}</strong> is processing. If you continue, you'll be submitting an additional payment.</span>
  );


  return (
    <div className="inner-container payment-review-view">
      <Header />
      <AppHeaderBackButton id="back-button" onClick={onBack} />
      <ThemeProvider>
        <Typography
          variant="h5"
          sx={{ fontWeight: '500', marginBottom: '14px' }}
          component="h1"
          className="payment-review-title"
        >
          Review your payment
        </Typography>
        {pit?.amount && pit?.date && (
          <Alert
            className="pit-alert"
            severity="warning"
            sx={{
              marginBottom: '16px',
            }}
            displayCloseButton={false}
          >
            {pitText}
          </Alert>
        )}
        <Typography variant="b1Regular" sx={{ marginBottom: '14px' }} className="payment-review-subtitle">
          If anything looks incorrect, select Back to edit your information on the previous screen.
        </Typography>
        <div id="payment-review-info-box">
          <div className="review-info-detail-box">
            <div className="review-info-box">
              <Typography variant="b1Regular" className="payment-type">
                {paymentType}
              </Typography>
              <Typography variant="b1Medium" className="last4">
                {/* Show icon + last4 from paymentDetails.card (works for all flows) */}
                {paymentDetails.card ? getIconForCard(paymentDetails.card.brand) : ''}
                {last4 !== '' ? ` •••• ${last4}` : ''}
              </Typography>
            </div>
            {paymentDetails.echeck?.routingNumber && (
              <div className="review-info-box" style={{ marginTop: '16px' }}>
                <Typography variant="b1Regular" className="routing-number-label">
                  Bank routing number:
                </Typography>
                <Typography variant="b1Medium" className="routing-number">
                  {eCheckRoutingNumber}
                </Typography>
              </div>
            )}
            {schedule && (
              <div className="review-info-box" style={{ marginTop: '16px' }}>
                <Typography variant="b1Regular" className="schedule-payment-label">
                  Scheduled payment date:
                </Typography>
                <Typography variant="b1Medium" className="schedule-payment-date">
                  {DateHelper.formatDateToLegible(scheduledDate || '')}
                </Typography>
              </div>
            )}
          </div>
          <div className="review-info-payment-box">
            <Typography variant="h6" component="p">
              Payment amount:{' '}
            </Typography>
            <Typography variant="h6" component="p">
              ${amount.toFixed(2)}
            </Typography>
          </div>
        </div>

        <Typography variant="b2Regular" id="authorization-text">
          By clicking {buttonText}, I hereby state that I am an authorized user for the above payment method, and have
          read and agree to the{' '}
          <Link
            data-id="payment-authorization-link"
            variant="b2Regular"
            component="button"
            sx={{ verticalAlign: 'baseline' }}
            onClick={() => {
              setShowPaymentAuthorization(true);
            }}
          >
            Payment Authorization
          </Link>
          .
        </Typography>
        <Button
          id="submit-button"
          variant="contained"
          fullWidth={true}
          label={buttonText}
          onClick={(e) => handleSubmit(e)}
          sx={{ marginBottom: '8px', marginTop: '16px' }}
        ></Button>
      </ThemeProvider>
      {showPaymentAuthorization && (
        <PaymentMethodsAuthorizationModal
          handleClose={() => {
            setShowPaymentAuthorization(false);
          }}
          ctaText={buttonText}
        ></PaymentMethodsAuthorizationModal>
      )}
    </div>
  );
};

const isFutureDate = (date: string | undefined) => {
  if (!date) return false;
  const today = new Date();
  const parsedDate = new Date(date);

  const todayWithoutTime = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const parsedDateWithoutTime = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
  return parsedDateWithoutTime?.getTime() > todayWithoutTime.getTime();
};
